// src/components/MyCourses.js — calm course list.
//
// Per ui-design-system.md: density via lists, status cues via small dots,
// single accent. Drop decorative avatar tiles and per-row domains/concepts
// counts (they live on the course detail page, not here).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';

import { apiService, handleApiError } from '../services/api';
import Loading from './common/Loading';
import usePageTitle from '../hooks/usePageTitle';
import { Button, Card, PageTitle } from './ui';
import { CourseCard } from './ui/dashboard';
import { useConfirm } from './ui/ConfirmDialog';

const SAMPLE_CODES = new Set(['BASICW', 'CKA']);

export default function MyCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [summary, setSummary] = useState({});
  useEffect(() => {
    let alive = true;
    apiService.getCoursesSummary()
      .then((res) => { if (alive) setSummary(res?.data?.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  usePageTitle('My Courses');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getUserCourses();
      setCourses(res.data || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (course) => {
    const ok = await confirm({
      title: `Delete “${course.name}”?`,
      body: 'This archives the course — its history is preserved but it disappears from this list.',
      confirmLabel: 'Delete course',
      danger: true,
    });
    if (!ok) return;
    setDeletingId(course.id || course.code);
    try {
      await apiService.deleteCourse(course.id || course.code);
      setCourses((cs) => cs.filter((c) => (c.id || c.code) !== (course.id || course.code)));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <Loading message="Loading your courses..." />;

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <header className="flex items-start justify-between mb-8">
          <div>
            <PageTitle>My Courses</PageTitle>
            <p className="mt-2 text-sm text-mutedink">
              Pick one to keep practicing.
            </p>
          </div>
          <Button to="/create-course" variant="primary">
            <Plus className="w-4 h-4" />
            New course
          </Button>
        </header>

        {error && (
          <div className="mb-6 p-3 rounded-lg border border-line bg-surface text-sm text-status-bad flex items-center justify-between gap-3">
            <span>{error}</span>
            <button
              type="button"
              onClick={load}
              className="text-accent hover:text-accent-hover font-medium text-xs"
            >
              Retry
            </button>
          </div>
        )}

        {courses.length === 0 ? (
          <Card className="p-10 text-center">
            <p className="text-base text-read mb-2">No courses yet.</p>
            <p className="text-sm text-mutedink mb-6">
              Tell us what you want to learn — we'll set it up.
            </p>
            <Button to="/tutor" variant="primary">
              Start with the assistant
            </Button>
          </Card>
        ) : (
          <div className="lib-grid">
            {courses.map((c) => {
              const id = c.id || c.code;
              const isSample = SAMPLE_CODES.has(c.code);
              const isDeleting = deletingId === id;
              return (
                <div key={id} className={isDeleting ? 'opacity-50 relative' : 'relative'}>
                  <CourseCard
                    c={{
                      code: c.code,
                      name: c.name,
                      description: c.description,
                      status: isSample ? 'fresh' : 'active',
                      concepts: (c.domains || []).reduce((n, d) => n + (d.concepts?.length || 0), 0),
                      dist: summary[c.id || c.code]?.tested ? summary[c.id || c.code].dist : undefined,
                      due: summary[c.id || c.code]?.due || 0,
                      blockers: summary[c.id || c.code]?.blockers || 0,
                    }}
                    onOpen={() => navigate(`/course/${c.code}`)}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                    disabled={isDeleting}
                    aria-label={`Delete ${c.name}`}
                    className="absolute top-3 right-3 p-1.5 text-faint hover:text-status-bad disabled:opacity-30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
